import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { money, priceForPO, itemTaxRate } from '../purchase-orders/poLine';
import { getOrgLocale } from '../../../lib/locale';

export default function ReorderTab({ suppliers }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/general-inventory/reorder').then((r) => {
      setRows((r.data || []).map((i) => ({ ...i, qty: i.suggested_qty, supplier_id: i.preferred_supplier?.supplier_id || '' })));
    });
  }, []);

  const upd = (id, k, v) => setRows(rows.map((r) => (r.id === id ? { ...r, [k]: v } : r)));
  const chosen = rows.filter((r) => sel[r.id]);

  const raise = async () => {
    if (chosen.some((r) => !r.supplier_id)) { toast.error('Pick a supplier for every selected item'); return; }
    const groups = {};
    chosen.forEach((r) => { (groups[r.supplier_id] = groups[r.supplier_id] || []).push(r); });
    setBusy(true);
    try {
      for (const [sid, list] of Object.entries(groups)) {
        const lead = Math.max(0, ...list.map((r) => (r.suppliers || []).find((s) => s.supplier_id === sid)?.lead_time_days || 0));
        const date = new Date(Date.now() + lead * 86400000).toISOString().split('T')[0];
        await api.post('/purchase-orders', {
          supplier_id: sid, po_type: 'General', expected_delivery_date: date,
          line_items: list.map((r) => {
            const s = (r.suppliers || []).find((x) => x.supplier_id === sid) || {};
            return { item_id: r.id, sku: r.sku, description: r.name, unit: s.order_unit || r.unit, supplier_item_code: s.supplier_item_code || '',
              quantity_ordered: Number(r.qty) || 1, price_per_unit: priceForPO(s.price ?? s.last_price ?? r.cost_price ?? 0, r, getOrgLocale().po_prices_include_tax), tax_rate: itemTaxRate(r) };
          }),
        });
      }
      toast.success(`${Object.keys(groups).length} draft purchase order(s) created`);
      navigate('/purchase-orders');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create purchase orders'); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="gi-reorder-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">Items at or below their reorder level (stock on hand + already on order). Tick items, check the supplier and quantity, then raise draft POs — one per supplier.</p>
        <Button className="bg-slate-900" disabled={!chosen.length || busy} onClick={raise} data-testid="gi-raise-pos"><ShoppingCart className="h-4 w-4 mr-1" /> Raise {chosen.length || ''} PO line{chosen.length === 1 ? '' : 's'}</Button>
      </div>
      <Card><CardContent className="pt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100"><tr>
            <th className="px-2 py-2 w-8"><Checkbox checked={rows.length > 0 && chosen.length === rows.length} onCheckedChange={(v) => setSel(v ? Object.fromEntries(rows.map((r) => [r.id, true])) : {})} data-testid="gi-reorder-all" /></th>
            {['Item', 'On hand', 'On order', 'Reorder at', 'Order qty', 'Supplier', 'Price'].map((h) => <th key={h} className="text-left px-2 py-2">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-500">Nothing needs reordering. Set a reorder level on items to see them here.</td></tr>}
            {rows.map((r) => {
              const s = (r.suppliers || []).find((x) => x.supplier_id === r.supplier_id) || {};
              return (
                <tr key={r.id} className="border-t" data-testid={`gi-reorder-row-${r.id}`}>
                  <td className="px-2 py-2"><Checkbox checked={!!sel[r.id]} onCheckedChange={(v) => setSel({ ...sel, [r.id]: !!v })} data-testid={`gi-reorder-check-${r.id}`} /></td>
                  <td className="px-2 py-2 font-medium">{r.name}<div className="text-xs text-slate-500">{r.sku}</div></td>
                  <td className="px-2 py-2 text-red-600 font-semibold">{r.quantity_on_hand} {r.unit}</td>
                  <td className="px-2 py-2">{r.on_order || '—'}</td>
                  <td className="px-2 py-2">{r.reorder_level}</td>
                  <td className="px-2 py-2"><Input className="h-8 w-24" type="number" step="any" value={r.qty} onChange={(e) => upd(r.id, 'qty', e.target.value)} data-testid={`gi-reorder-qty-${r.id}`} /></td>
                  <td className="px-2 py-2">
                    <select className="border rounded-md h-8 px-2 text-sm" value={r.supplier_id} onChange={(e) => upd(r.id, 'supplier_id', e.target.value)} data-testid={`gi-reorder-supplier-${r.id}`}>
                      <option value="">Select…</option>
                      {(r.suppliers || []).map((x) => <option key={x.supplier_id} value={x.supplier_id}>{x.supplier_name}{x.preferred ? ' ★' : ''}</option>)}
                      {suppliers.filter((x) => !(r.suppliers || []).some((y) => y.supplier_id === x.id)).map((x) => <option key={x.id} value={x.id}>{x.company_name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2">{money(s.price ?? s.last_price ?? r.cost_price)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
